import { useRef } from "react";
import axios from "axios";
import { API, errText } from "@/lib/api";
import { compressImage } from "@/lib/images";
import { track } from "@/lib/tracking";
import { StepHeading } from "@/components/landing/FormSteps";
import { Camera, X, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const MAX_PHOTOS = 8;

// Photos upload immediately on selection (compressed client-side, re-encoded server-side) and are
// attached to the lead when the form is submitted.
export const PhotoStep = ({ photos, setPhotos }) => {
  const inputRef = useRef();
  const firstDone = useRef(false);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, MAX_PHOTOS - photos.length);
    if (!files.length) {
      if (fileList?.length) toast.error(`You can add up to ${MAX_PHOTOS} photos.`);
      return;
    }
    const staged = files.map((file) => ({ key: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`, file, url: URL.createObjectURL(file), id: null, uploading: true, error: null }));
    setPhotos((p) => [...p, ...staged]);
    for (const s of staged) {
      try {
        const fd = new FormData();
        fd.append("files", await compressImage(s.file));
        const { data } = await axios.post(`${API}/public/leads/photos`, fd);
        setPhotos((p) => p.map((x) => (x.key === s.key ? { ...x, id: data.photos[0].id, uploading: false } : x)));
        if (!firstDone.current) {
          firstDone.current = true;
          track("PhotoUploadCompleted");
        }
      } catch (e) {
        setPhotos((p) => p.map((x) => (x.key === s.key ? { ...x, uploading: false, error: errText(e) } : x)));
      }
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (key) => {
    setPhotos((p) => {
      const gone = p.find((x) => x.key === key);
      if (gone) URL.revokeObjectURL(gone.url);
      return p.filter((x) => x.key !== key);
    });
  };

  return (
    <div>
      <StepHeading title="Show us your furniture." sub="Upload 2–5 clear photos so our team can determine whether Revival is likely to be the right service." />
      <label
        data-testid="photo-dropzone"
        className="flex flex-col items-center justify-center gap-3 border border-dashed border-gray-400 hover:border-black bg-[#FAF7F2] px-6 py-10 cursor-pointer transition-colors duration-150 text-center"
      >
        <Camera size={28} strokeWidth={1.5} className="text-[#c19a6b]" />
        <span className="text-[15px] font-semibold">Tap to add photos</span>
        <span className="text-xs text-gray-500">Take new photos or choose from your library · JPG, PNG or HEIC</span>
        <input
          ref={inputRef}
          data-testid="photo-input"
          type="file"
          accept="image/*,.heic,.heif"
          multiple
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
      </label>
      {photos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-4" data-testid="photo-thumbs">
          {photos.map((p) => (
            <div key={p.key} className="relative aspect-square bg-gray-100 overflow-hidden" data-testid={`photo-thumb${p.id ? "-ready" : p.error ? "-error" : "-uploading"}`}>
              <img src={p.url} alt="Your furniture" className={`w-full h-full object-cover ${p.uploading ? "opacity-50" : ""}`} />
              {p.uploading && <Loader2 size={22} className="absolute inset-0 m-auto text-black animate-spin" />}
              {p.error && (
                <div className="absolute inset-0 bg-red-600/85 text-white text-[10px] leading-tight p-2 flex flex-col items-center justify-center text-center gap-1">
                  <AlertCircle size={16} /> {p.error}
                </div>
              )}
              <button type="button" aria-label="Remove photo" onClick={() => remove(p.key)} className="absolute top-1 right-1 w-7 h-7 bg-black/75 text-white flex items-center justify-center hover:bg-black transition-colors duration-150">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-gray-500 mt-4 leading-relaxed">
        Tip: one photo of the whole piece plus close-ups of the worn areas gives us the clearest picture.
      </p>
    </div>
  );
};
